import { createContext, useContext, useEffect, useRef, useState } from "react";

import { wasMentionPopupOpen } from "src/components/comments/mention-popup";

/** The comment cards open on a surface, newest last, each with how to close it. */
type CommentLayers = {
  /** Returns the function that removes it again. */
  push: (close: () => void) => () => void;
  top: () => (() => void) | null;
};

function createCommentLayers(): CommentLayers {
  const closers: (() => void)[] = [];
  return {
    push: (close) => {
      closers.push(close);
      return () => {
        const index = closers.lastIndexOf(close);
        if (index !== -1) closers.splice(index, 1);
      };
    },
    top: () => closers.at(-1) ?? null,
  };
}

const CommentLayersContext = createContext<CommentLayers | null>(null);

export const CommentLayersProvider = CommentLayersContext.Provider;

/** Puts an open comment card on its surface's Escape stack, so Escape closes it before anything under it. Null while closed. */
export function useCommentLayer(close: (() => void) | null) {
  const layers = useContext(CommentLayersContext);
  const closeRef = useRef(close);
  closeRef.current = close;
  const isOpen = close !== null;
  useEffect(() => {
    if (!layers || !isOpen) return;
    return layers.push(() => closeRef.current?.());
  }, [layers, isOpen]);
}

type Args = {
  isCommentMode: boolean;
  setCommentMode: (isOn: boolean) => void;
  /** Off while something over the surface, like a file open on a canvas, has the keyboard. Modals always do. */
  isEnabled?: boolean;
  /** Closes the comment the surface itself has open, under any card a child put on the stack. Null when none is open. */
  closeOpenComment?: (() => void) | null;
  /** Holds Escape for a gesture that takes it first, like dragging a pin. */
  isEscapeHeld?: () => boolean;
  /** Takes focus back after each step, so the next Escape reaches the surface's own exit. */
  focusRoot?: () => HTMLElement | null;
};

/**
 * A surface's comment keys: C toggles comment mode, and Escape unwinds one
 * layer per press, an open menu, the mention list, the newest open card,
 * then comment mode. Both act in the capture phase and mark the press handled, so a
 * page-wide shortcut on the same key can stand down. Provide the returned
 * layers around the surface's comment cards.
 */
export function useCommentKeys({
  isCommentMode,
  setCommentMode,
  isEnabled = true,
  closeOpenComment = null,
  isEscapeHeld,
  focusRoot,
}: Args): CommentLayers {
  const [layers] = useState(createCommentLayers);
  // Read when the key lands, so the listeners stay put across renders.
  const latest = useRef({
    isCommentMode,
    setCommentMode,
    closeOpenComment,
    isEscapeHeld,
    focusRoot,
  });
  latest.current = {
    isCommentMode,
    setCommentMode,
    closeOpenComment,
    isEscapeHeld,
    focusRoot,
  };

  useEffect(() => {
    if (!isEnabled) return;
    // A modal over the surface keeps its own keys.
    const isModalOpen = () =>
      document.querySelector('[aria-modal="true"]') !== null;
    // Reads what was open before a popover's own Escape closes it, and the press goes no further.
    const onEscape = (event: KeyboardEvent) => {
      const current = latest.current;
      if (
        event.key !== "Escape" ||
        isModalOpen() ||
        isInMenu(event.target) ||
        wasMentionPopupOpen(event) ||
        current.isEscapeHeld?.()
      ) {
        return;
      }
      const close = layers.top() ?? current.closeOpenComment;
      if (close) close();
      else if (current.isCommentMode) current.setCommentMode(false);
      else return;
      event.preventDefault();
      event.stopPropagation();
      const root = current.focusRoot?.();
      // After the closed card has unmounted.
      if (root)
        requestAnimationFrame(() => root.focus({ preventScroll: true }));
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.repeat ||
        event.key.toLowerCase() !== "c" ||
        event.shiftKey ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        isModalOpen() ||
        isInMenu(event.target) ||
        isTextField(event.target)
      ) {
        return;
      }
      event.preventDefault();
      latest.current.setCommentMode(!latest.current.isCommentMode);
    };
    window.addEventListener("keydown", onEscape, true);
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onEscape, true);
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [isEnabled, layers]);

  return layers;
}

function isTextField(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable ||
      ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
  );
}

/** A menu or list with focus takes its own keys, Escape closing it first. */
function isInMenu(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest('[role="menu"], [role="listbox"]') !== null
  );
}
